import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';

import { urlApplicationRoot } from '/business/shared/util/Statics';

import useUrlBuilder from '/business/shared/hooks/useUrlBuilder';

import type { RootState } from '/business/shared/state/store';

import { getReductionPlanVersionSelector } from '/business/shared/state/selectors/application/basket_meta/getBasketMetaSelector';
import getApplicationIdSelector from '/business/shared/state/selectors/application/getApplicationIdSelector';
import getSiblingApplicationsSelector from '/business/shared/state/selectors/application/model/getSiblingApplicationsSelector';
import isZeroPaymentExcludingFullyContributedBehaviourSelector from '/business/shared/state/selectors/application/payment/isZeroPaymentExcludingFullyContributedBehaviourSelector';
import isReducePageUnlockedSelector from '/business/shared/state/selectors/application/isReducePageUnlockedSelector';
import isConfirmedCompletedSelector from '/business/shared/state/selectors/application/workflow/isConfirmedCompletedSelector';
import isFormCompletedSelector from '/business/shared/state/selectors/application/workflow/isFormCompletedSelector';
import isReadyForPaymentSelector from '/business/shared/state/selectors/application/workflow/isReadyForPaymentSelector';
import isReduceCompletedSelector from '/business/shared/state/selectors/application/workflow/isReduceCompletedSelector';
import hasActiveApplicationAnnualUpdateSelector from '/business/shared/state/selectors/applications/hasActiveApplicationAnnualUpdateSelector';
import isQuoteAvailableSelector from '/business/shared/state/selectors/payment/quote/isQuoteAvailableSelector';

import StyledHeaderTabs from '/business/shared/components/widgets/header/__styled__/StyledHeaderTabs';
import StyledHeaderTabsListItem from '/business/shared/components/widgets/header/__styled__/StyledHeaderTabsListItem';
import StyledHeaderTabsList from '/business/shared/components/widgets/header/__styled__/StyledHeaderTabsList';
import getApplicationCarbonCreditServicesEnabledSelector from '/business/shared/state/selectors/application/model/getApplicationCarbonCreditServicesEnabledSelector';

import type { HeaderApplicationTabsProps, HeaderTabItem } from '../types';

import IconPay from '/business/shared/images/icons/Card-white.svg';
import IconGraph from '/business/shared/images/icons/bp-graph-white.svg';
import IconChecklist from '/business/shared/images/icons/bp-tick-checklist-white.svg';
import IconLeaf from '/business/shared/images/icons/bp-shoot-white.svg';
import IconAnnualUpdates from '/business/shared/images/icons/icon-annual-updates.svg';

function HeaderApplicationTabs(ownProps: HeaderApplicationTabsProps) {
  const applicationId = useSelector(getApplicationIdSelector);
  const isFormCompleted =
    useSelector((state: RootState) =>
      isFormCompletedSelector(state, { ...ownProps, includesNoData: true, includesSubQuestions: true }),
    ) ?? null;
  const isReduceCompleted = useSelector(isReduceCompletedSelector) || false;
  const isConfirmedCompleted = useSelector(isConfirmedCompletedSelector) || false;
  const isReadyForPayment = useSelector(isReadyForPaymentSelector);
  const isQuoteAvailable = useSelector(isQuoteAvailableSelector) || false;
  const isZeroPaymentExcludingFullyContributedBehaviour =
    useSelector(isZeroPaymentExcludingFullyContributedBehaviourSelector) || false;
  const stateReductionPlanVersion = useSelector(getReductionPlanVersionSelector);
  const hasAnnualUpdate = useSelector(hasActiveApplicationAnnualUpdateSelector);
  const carbonCreditServicesEnabled = useSelector(getApplicationCarbonCreditServicesEnabledSelector);
  const { t } = useTranslation();
  const location = useLocation();
  const { pathname } = location;
  const { getApplicationUrl } = useUrlBuilder();

  const stateIsReductionOnboarded = useSelector(isReducePageUnlockedSelector);
  const siblingApplications = useSelector(getSiblingApplicationsSelector);
  const hasMultipleReportingPeriods = siblingApplications && siblingApplications.length > 1;

  // @todo This can be done cleaner.
  if (pathname.match(/(getting-started)$/i)) {
    return null;
  }

  const wasApplicationCompletedPriorToPayments = !isQuoteAvailable && isConfirmedCompleted;
  const isPayment = !isZeroPaymentExcludingFullyContributedBehaviour && !wasApplicationCompletedPriorToPayments;

  const isOnAnnualUpdatesPage = pathname.startsWith(`/${urlApplicationRoot}/annual-updates`);

  const tabs: Record<string, HeaderTabItem> = {
    ...(hasAnnualUpdate
      ? {
          annualUpdates: {
            key: 'annual-updates',
            title: 'Get started with your annual updates',
            isActive: isOnAnnualUpdatesPage,
            href: getApplicationUrl('/annual-updates', applicationId),
            icon: IconAnnualUpdates,
          },
        }
      : {}),
    emissions: {
      key: 'emissions',
      title: t('business:Dashboard.applicationTabs.emissions.title', 'Calculate your carbon emissions'),
      isActive:
        pathname.startsWith(`/${urlApplicationRoot}`) &&
        !pathname.match(/(annual-updates|reduce|reduce\/.*|payment|submit|confirmed|offsets)$/i),
      href: getApplicationUrl('', applicationId),
      isCompleted: isFormCompleted,
      icon: IconGraph,
    },
    reduce: {
      key: 'reduce',
      title: t('business:Dashboard.applicationTabs.reduce.title', 'Create your reduction plan'),
      isActive: pathname.startsWith(`/${urlApplicationRoot}/reduce`),
      href: getApplicationUrl(
        `/reduce/${stateIsReductionOnboarded ? (hasMultipleReportingPeriods ? 'comparison' : 'hotspots') : 'onboarding'}`,
        applicationId,
      ),
      icon: IconChecklist,
      //     isDisabled: isReductionPageBlocked,
      // was added to help airbp display correctly, causing too many issues with legacy applications
      // we need instead to write a check that anything has been entered...
    },
    ...(carbonCreditServicesEnabled
      ? {
          optionalOffset: {
            key: 'confirmed-optional-combined',
            title: t('business:Dashboard.applicationTabs.optionalOffset.title', 'Optional: Carbon credit services'),
            isActive: pathname.startsWith(`/${urlApplicationRoot}/offsets`),
            href: getApplicationUrl('/offsets', applicationId),
            icon: IconLeaf,
          },
        }
      : {}),
    payment: {
      key: 'payment',
      title: t('business:Dashboard.applicationTabs.payment.title', 'Payment'),
      isActive: pathname.startsWith(`/${urlApplicationRoot}/payment`),
      href: getApplicationUrl('/payment', applicationId),
      icon: IconPay,
    },
  };

  if (!carbonCreditServicesEnabled || !isPayment || !isQuoteAvailable) {
    delete tabs.payment;
  }

  if (!isConfirmedCompleted && !isReadyForPayment) {
    delete tabs.payment;
  }
  if (stateReductionPlanVersion === 3 && isConfirmedCompleted) {
    delete tabs.payment;
  }

  return (
    <StyledHeaderTabs>
      <StyledHeaderTabsList>
        {Object.values(tabs).map((item) => {
          return (
            <StyledHeaderTabsListItem
              key={item.key}
              $isApplicationListItemStyle
              data-test-id={`apptab-${item.key}`}
              $icon={item.icon}
              $isActive={item.isActive}
              $isDisabled={item.isDisabled}
              $isCompleted={item.isCompleted}
              $isIconOnly={Boolean(item.isIconOnly)}
              title={
                item.isDisabled
                  ? t(
                      'business:Dashboard.applicationTabs.stepUnavailable',
                      'This tab is not available yet, please ensure you have completed the previous section',
                    )
                  : item.title
              }
            >
              <Link to={item.href}>
                <div>{item.title}</div>
                <div>Icon</div>
              </Link>
            </StyledHeaderTabsListItem>
          );
        })}
      </StyledHeaderTabsList>
    </StyledHeaderTabs>
  );
}

export default HeaderApplicationTabs;
